/**
 * PATH: apps/hr/src/pages/leave/components/LeaveRequestsTable.tsx
 * PURPOSE: Leave requests table with approve/reject actions
 */

import { Check, X } from 'lucide-react'
import { cn, formatDate } from '../../../lib/utils'
import { calculateDaysInclusive, type LeaveRequestApi } from './types'

const statusStyles: Record<string, string> = {
  pending: 'bg-amber-100 text-amber-700 dark:bg-amber-900/30 dark:text-amber-400',
  approved: 'bg-emerald-100 text-emerald-700 dark:bg-emerald-900/30 dark:text-emerald-400',
  rejected: 'bg-red-100 text-red-700 dark:bg-red-900/30 dark:text-red-400',
  cancelled: 'bg-slate-100 text-slate-600 dark:bg-slate-800 dark:text-slate-300',
}

export function LeaveRequestsTable({
  requests,
  onApprove,
  onReject,
  isMutating,
}: {
  requests: LeaveRequestApi[]
  onApprove: (id: string) => void
  onReject: (id: string) => void
  isMutating: boolean
}) {
  return (
    <div className="overflow-x-auto">
      <table className="w-full">
        <thead>
          <tr className="border-b border-slate-200 dark:border-slate-700 bg-slate-50 dark:bg-slate-800/50">
            <th className="text-left text-xs font-medium text-slate-500 uppercase tracking-wider py-3 px-4">Employee</th>
            <th className="text-left text-xs font-medium text-slate-500 uppercase tracking-wider py-3 px-4">Type</th>
            <th className="text-left text-xs font-medium text-slate-500 uppercase tracking-wider py-3 px-4">Start</th>
            <th className="text-left text-xs font-medium text-slate-500 uppercase tracking-wider py-3 px-4">End</th>
            <th className="text-left text-xs font-medium text-slate-500 uppercase tracking-wider py-3 px-4">Duration</th>
            <th className="text-left text-xs font-medium text-slate-500 uppercase tracking-wider py-3 px-4">Status</th>
            <th className="text-left text-xs font-medium text-slate-500 uppercase tracking-wider py-3 px-4">Actions</th>
          </tr>
        </thead>
        <tbody className="divide-y divide-slate-100 dark:divide-slate-800">
          {requests.map((leave) => (
            <tr key={leave.id} className="hover:bg-slate-50 dark:hover:bg-slate-800/50">
              <td className="py-3 px-4">
                <div className="flex items-center gap-3">
                  <div className="w-8 h-8 rounded-full bg-slate-200 dark:bg-slate-700 flex items-center justify-center text-xs font-medium">
                    {leave.worker_name.split(' ').map(n => n[0]).join('')}
                  </div>
                  <span className="text-sm font-medium text-slate-900 dark:text-white">{leave.worker_name}</span>
                </div>
              </td>
              <td className="py-3 px-4 text-sm text-slate-600 dark:text-slate-400">{leave.leave_type_name}</td>
              <td className="py-3 px-4 text-sm text-slate-600 dark:text-slate-400">
                {formatDate(leave.start_date, { day: 'numeric', month: 'short', year: 'numeric' })}
              </td>
              <td className="py-3 px-4 text-sm text-slate-600 dark:text-slate-400">
                {formatDate(leave.end_date, { day: 'numeric', month: 'short', year: 'numeric' })}
              </td>
              <td className="py-3 px-4 text-sm text-slate-600 dark:text-slate-400">
                {calculateDaysInclusive(leave.start_date, leave.end_date)}d • {Math.round(leave.hours_requested)}h
              </td>
              <td className="py-3 px-4">
                <span className={cn('px-2.5 py-1 rounded-full text-xs font-medium capitalize', statusStyles[leave.status] || 'bg-slate-100 text-slate-600 dark:bg-slate-800 dark:text-slate-300')}>
                  {leave.status}
                </span>
              </td>
              <td className="py-3 px-4">
                {leave.status === 'pending' ? (
                  <div className="flex items-center gap-1">
                    <button onClick={() => onApprove(leave.id)} disabled={isMutating} className="p-1.5 hover:bg-emerald-100 dark:hover:bg-emerald-900/30 rounded-lg text-emerald-600 dark:text-emerald-400 disabled:opacity-60" title="Approve">
                      <Check className="w-4 h-4" />
                    </button>
                    <button onClick={() => onReject(leave.id)} disabled={isMutating} className="p-1.5 hover:bg-red-100 dark:hover:bg-red-900/30 rounded-lg text-red-600 dark:text-red-400 disabled:opacity-60" title="Reject">
                      <X className="w-4 h-4" />
                    </button>
                  </div>
                ) : (
                  <span className="text-sm text-slate-400">—</span>
                )}
              </td>
            </tr>
          ))}
        </tbody>
      </table>
      {requests.length === 0 && (
        <div className="py-12 text-center text-sm text-slate-400">No leave requests found</div>
      )}
    </div>
  )
}
